import { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ChevronDown, Bookmark, X } from "lucide-react";
import axios from "axios";
import { AppContext, API } from "@/App";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const GUATEMALA_DEPARTMENTS = [
  "Guatemala", "Alta Verapaz", "Baja Verapaz", "Chimaltenango", 
  "Chiquimula", "Escuintla", "Huehuetenango", "Izabal",
  "Jalapa", "Jutiapa", "Petén", "Quetzaltenango", "Quiché",
  "Retalhuleu", "Sacatepéquez", "San Marcos", "Santa Rosa",
  "Sololá", "Suchitepéquez", "Totonicapán", "Zacapa"
];

const PROPERTY_TYPES = ["Casa", "Apartamento", "Oficina", "Bodega", "Terreno", "Finca"];

// Quick-select price ranges (USD)
const PRICE_RANGES = [
  { label: "< $100K", min: 0, max: 100000 },
  { label: "$100K-$300K", min: 100000, max: 300000 },
  { label: "$300K-$500K", min: 300000, max: 500000 },
  { label: "$500K+", min: 500000, max: null },
];

// Quick-select bedroom options
const BEDROOM_OPTIONS = [
  { label: "1+", value: "1" },
  { label: "2+", value: "2" },
  { label: "3+", value: "3" },
  { label: "4+", value: "4" },
];

const SearchBar = ({ onSearch, compact = false, showSaveSearch = true }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useContext(AppContext);
  
  const [filters, setFilters] = useState({
    transaction_type: "",
    property_type: "",
    department: "",
    zone: "",
    min_price: "",
    max_price: "",
    min_area: "",
    max_area: "",
    min_bedrooms: "",
    min_bathrooms: "",
    min_parking: "",
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [availableZones, setAvailableZones] = useState([]);
  const [loadingZones, setLoadingZones] = useState(false);
  const [selectedPriceRange, setSelectedPriceRange] = useState(null);
  const [selectedBedrooms, setSelectedBedrooms] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveEmail, setSaveEmail] = useState("");
  const [savingSearch, setSavingSearch] = useState(false);

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(v => v !== "") || 
    selectedPriceRange !== null || selectedBedrooms !== null;

  // Fetch zones when department changes
  useEffect(() => {
    const fetchZones = async () => {
      setLoadingZones(true);
      try {
        const params = filters.department ? `?department=${filters.department}` : "";
        const response = await axios.get(`${API}/zones${params}`);
        setAvailableZones(response.data.zones || []);
      } catch (error) {
        setAvailableZones([]);
      } finally {
        setLoadingZones(false);
      }
    };

    fetchZones();
  }, [filters.department]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      // Clear zone when department changes
      if (key === "department") {
        newFilters.zone = "";
      }
      return newFilters;
    });
  };

  // Handle quick price range selection
  const handlePriceRangeSelect = (index) => {
    if (selectedPriceRange === index) {
      // Deselect
      setSelectedPriceRange(null);
      setFilters(prev => ({ ...prev, min_price: "", max_price: "" }));
    } else {
      setSelectedPriceRange(index);
      const range = PRICE_RANGES[index];
      setFilters(prev => ({
        ...prev,
        min_price: range.min.toString(),
        max_price: range.max ? range.max.toString() : ""
      }));
    }
  };

  // Handle quick bedroom selection
  const handleBedroomSelect = (index) => {
    if (selectedBedrooms === index) {
      // Deselect
      setSelectedBedrooms(null);
      setFilters(prev => ({ ...prev, min_bedrooms: "" }));
    } else {
      setSelectedBedrooms(index);
      setFilters(prev => ({
        ...prev,
        min_bedrooms: BEDROOM_OPTIONS[index].value
      }));
    }
  };

  // Save search with email for alerts
  const handleSaveSearch = async () => {
    if (!saveEmail.trim() || !saveEmail.includes("@")) {
      toast.error(t('search.invalidEmail', 'Por favor ingresa un email válido'));
      return;
    }

    setSavingSearch(true);
    try {
      // Build search criteria
      const searchCriteria = {
        ...filters,
        price_range: selectedPriceRange !== null ? PRICE_RANGES[selectedPriceRange] : null,
      };
      
      // Remove empty values
      Object.keys(searchCriteria).forEach(key => {
        if (searchCriteria[key] === "" || searchCriteria[key] === null) {
          delete searchCriteria[key];
        }
      });

      await axios.post(`${API}/saved-searches`, {
        email: saveEmail.toLowerCase().trim(),
        criteria: searchCriteria,
        name: generateSearchName(searchCriteria)
      });

      toast.success(t('search.searchSaved', '¡Búsqueda guardada! Te notificaremos cuando haya propiedades nuevas.'));
      setShowSaveModal(false);
      setSaveEmail("");
    } catch (error) {
      if (error.response?.status === 409) {
        toast.error(t('search.searchExists', 'Ya tienes una búsqueda guardada con estos criterios'));
      } else {
        toast.error(t('search.saveError', 'Error al guardar la búsqueda'));
      }
    } finally {
      setSavingSearch(false);
    }
  };

  // Generate a friendly name for the saved search
  const generateSearchName = (criteria) => {
    const parts = [];
    if (criteria.property_type) parts.push(criteria.property_type);
    if (criteria.transaction_type) parts.push(criteria.transaction_type === 'Venta' ? 'Compra' : 'Alquiler');
    if (criteria.department) parts.push(criteria.department);
    if (criteria.min_bedrooms) parts.push(`${criteria.min_bedrooms}+ hab`);
    if (criteria.price_range) parts.push(PRICE_RANGES[selectedPriceRange]?.label);
    return parts.length > 0 ? parts.join(' - ') : 'Mi búsqueda';
  };

  const handleSearch = () => {
    // Build query params
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        // Convert price from GTQ to USD if needed for search
        if ((key === "min_price" || key === "max_price") && currency === "GTQ") {
          params.append(key, Math.round(parseFloat(value) / 7.80));
        } else if ((key === "min_area" || key === "max_area") && filters.area_unit === "varas2") {
          params.append(key, Math.round(parseFloat(value) / 1.4310));
        } else {
          params.append(key, value);
        }
      }
    });

    if (onSearch) {
      onSearch(filters);
    } else {
      navigate(`/propiedades?${params.toString()}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  return (
    <div className={`search-glass rounded-sm ${compact ? "p-3 sm:p-4" : "p-4 sm:p-6 md:p-8"}`} data-testid="search-bar">
      {/* Main filters row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        {/* Transaction Type */}
        <Select
          value={filters.transaction_type}
          onValueChange={(value) => handleFilterChange("transaction_type", value)}
        >
          <SelectTrigger 
            className="h-12 sm:h-14 bg-black/40 border-white/10 text-white text-sm sm:text-base"
            data-testid="filter-transaction-type"
          >
            <SelectValue placeholder={t('search.buyOrRent')} />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            <SelectItem value="Venta">{t('search.buy')}</SelectItem>
            <SelectItem value="Alquiler">{t('search.rent')}</SelectItem>
          </SelectContent>
        </Select>

        {/* Property Type */}
        <Select
          value={filters.property_type}
          onValueChange={(value) => handleFilterChange("property_type", value)}
        >
          <SelectTrigger 
            className="h-12 sm:h-14 bg-black/40 border-white/10 text-white text-sm sm:text-base"
            data-testid="filter-property-type"
          >
            <SelectValue placeholder={t('search.propertyType')} />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10">
            {PROPERTY_TYPES.map((type) => (
              <SelectItem key={type} value={type}>{type}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Department */}
        <Select
          value={filters.department}
          onValueChange={(value) => handleFilterChange("department", value)}
        >
          <SelectTrigger 
            className="h-12 sm:h-14 bg-black/40 border-white/10 text-white text-sm sm:text-base"
            data-testid="filter-department"
          >
            <SelectValue placeholder={t('search.department')} />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10 max-h-60">
            {GUATEMALA_DEPARTMENTS.map((dept) => (
              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Zone - Dynamic Select */}
        <Select
          value={filters.zone}
          onValueChange={(value) => handleFilterChange("zone", value)}
          disabled={loadingZones}
        >
          <SelectTrigger 
            className="h-12 sm:h-14 bg-black/40 border-white/10 text-white text-sm sm:text-base"
            data-testid="filter-zone"
          >
            <SelectValue placeholder={loadingZones ? t('common.loading') : t('search.zone')} />
          </SelectTrigger>
          <SelectContent className="bg-[#121212] border-white/10 max-h-60">
            {availableZones.length === 0 ? (
              <SelectItem value="_no_zones" disabled>
                {filters.department ? t('search.noZones') : t('search.selectDepartment')}
              </SelectItem>
            ) : (
              availableZones.map((zone) => (
                <SelectItem key={zone} value={zone}>{zone}</SelectItem>
              ))
            )}
          </SelectContent>
        </Select>

        {/* Search Button */}
        <Button
          onClick={handleSearch}
          className="h-12 sm:h-14 bg-[#D4AF37] text-black hover:bg-[#FFE082] font-medium uppercase tracking-wider text-sm sm:text-base sm:col-span-2 lg:col-span-1"
          data-testid="search-btn"
        >
          <Search className="mr-2" size={18} />
          {t('search.button')}
        </Button>
      </div>

      {/* Quick-select chips */}
      <div className="mt-4 space-y-3">
        {/* Price Range Chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-white/60 text-xs uppercase tracking-wider mr-2">
            {t('search.priceRange', 'Precio')}:
          </span>
          {PRICE_RANGES.map((range, index) => (
            <button
              key={index}
              onClick={() => handlePriceRangeSelect(index)}
              className={`px-3 py-1.5 text-xs sm:text-sm rounded-full transition-all ${
                selectedPriceRange === index
                  ? 'bg-[#D4AF37] text-black font-medium'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
              data-testid={`price-chip-${index}`}
            >
              {range.label}
            </button>
          ))}
        </div>

        {/* Bedroom Chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-white/60 text-xs uppercase tracking-wider mr-2">
            {t('search.bedrooms', 'Habitaciones')}:
          </span>
          {BEDROOM_OPTIONS.map((option, index) => (
            <button
              key={index}
              onClick={() => handleBedroomSelect(index)}
              className={`px-3 py-1.5 text-xs sm:text-sm rounded-full transition-all ${
                selectedBedrooms === index
                  ? 'bg-[#D4AF37] text-black font-medium'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
              data-testid={`bedroom-chip-${index}`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Advanced filters toggle */}
      <div className="mt-3 sm:mt-4 flex items-center justify-between">
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-xs sm:text-sm text-white/60 hover:text-[#D4AF37] transition-colors"
          data-testid="advanced-filters-toggle"
        >
          <ChevronDown size={16} className={`transition-transform ${showAdvanced ? "rotate-180" : ""}`} />
          {t('search.advancedFilters')}
        </button>

        {/* Save Search Button */}
        {showSaveSearch && hasActiveFilters && (
          <button
            onClick={() => setShowSaveModal(true)}
            className="flex items-center gap-2 text-xs sm:text-sm text-[#D4AF37] hover:text-[#FFE182] transition-colors"
            data-testid="save-search-btn"
          >
            <Bookmark size={16} />
            {t('search.saveSearch', 'Guardar búsqueda')}
          </button>
        )}
      </div>

      {/* Advanced filters */}
      {showAdvanced && (
        <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-white/10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4">
          {/* Price Range */}
          <Input
            type="number"
            placeholder={t('search.priceMin', { currency })}
            value={filters.min_price}
            onChange={(e) => handleFilterChange("min_price", e.target.value)}
            onKeyPress={handleKeyPress}
            className="h-10 sm:h-12 bg-black/40 border-white/10 text-white placeholder:text-white/40 text-xs sm:text-sm"
            data-testid="filter-min-price"
          />
          <Input
            type="number"
            placeholder={t('search.priceMax', { currency })}
            value={filters.max_price}
            onChange={(e) => handleFilterChange("max_price", e.target.value)}
            onKeyPress={handleKeyPress}
            className="h-12 bg-black/40 border-white/10 text-white placeholder:text-white/40 text-sm"
            data-testid="filter-max-price"
          />

          {/* Area Range */}
          <Input
            type="number"
            placeholder={t('search.areaMin')}
            value={filters.min_area}
            onChange={(e) => handleFilterChange("min_area", e.target.value)}
            onKeyPress={handleKeyPress}
            className="h-12 bg-black/40 border-white/10 text-white placeholder:text-white/40 text-sm"
            data-testid="filter-min-area"
          />
          <Input
            type="number"
            placeholder={t('search.areaMax')}
            value={filters.max_area}
            onChange={(e) => handleFilterChange("max_area", e.target.value)}
            onKeyPress={handleKeyPress}
            className="h-12 bg-black/40 border-white/10 text-white placeholder:text-white/40 text-sm"
            data-testid="filter-max-area"
          />

          {/* Rooms */}
          <Select
            value={filters.min_bedrooms}
            onValueChange={(value) => handleFilterChange("min_bedrooms", value)}
          >
            <SelectTrigger 
              className="h-12 bg-black/40 border-white/10 text-white text-sm"
              data-testid="filter-bedrooms"
            >
              <SelectValue placeholder="Habitaciones" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              {[1, 2, 3, 4, 5, 6].map((num) => (
                <SelectItem key={num} value={num.toString()}>{num}+ hab</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Bathrooms */}
          <Select
            value={filters.min_bathrooms}
            onValueChange={(value) => handleFilterChange("min_bathrooms", value)}
          >
            <SelectTrigger 
              className="h-12 bg-black/40 border-white/10 text-white text-sm"
              data-testid="filter-bathrooms"
            >
              <SelectValue placeholder="Baños" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              {[1, 2, 3, 4, 5].map((num) => (
                <SelectItem key={num} value={num.toString()}>{num}+ baños</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Parking */}
          <Select
            value={filters.min_parking}
            onValueChange={(value) => handleFilterChange("min_parking", value)}
          >
            <SelectTrigger 
              className="h-12 bg-black/40 border-white/10 text-white text-sm"
              data-testid="filter-parking"
            >
              <SelectValue placeholder="Parqueos" />
            </SelectTrigger>
            <SelectContent className="bg-[#121212] border-white/10">
              {[1, 2, 3, 4, 5].map((num) => (
                <SelectItem key={num} value={num.toString()}>{num}+ parqueos</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Clear Filters */}
          <Button
            variant="outline"
            onClick={() => {
              setFilters({
                transaction_type: "",
                property_type: "",
                department: "",
                zone: "",
                min_price: "",
                max_price: "",
                min_area: "",
                max_area: "",
                min_bedrooms: "",
                min_bathrooms: "",
                min_parking: "",
              });
              setSelectedPriceRange(null);
              setSelectedBedrooms(null);
            }}
            className="h-12 border-white/10 text-white/60 hover:text-white hover:bg-white/5 text-sm"
            data-testid="clear-filters-btn"
          >
            Limpiar filtros
          </Button>
        </div>
      )}

      {/* Save Search Modal */}
      {showSaveModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-white text-lg font-medium flex items-center gap-2">
                <Bookmark size={20} className="text-[#D4AF37]" />
                {t('search.saveSearchTitle', 'Guardar Búsqueda')}
              </h3>
              <button
                onClick={() => setShowSaveModal(false)}
                className="text-white/60 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            
            <p className="text-[#A3A3A3] text-sm mb-4">
              {t('search.saveSearchDescription', 'Te notificaremos por email cuando nuevas propiedades coincidan con tu búsqueda.')}
            </p>

            {/* Show active filters summary */}
            <div className="bg-black/40 p-3 rounded-lg mb-4">
              <p className="text-white/60 text-xs uppercase tracking-wider mb-2">
                {t('search.activeFilters', 'Filtros activos')}:
              </p>
              <div className="flex flex-wrap gap-2">
                {filters.transaction_type && (
                  <span className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs px-2 py-1 rounded">
                    {filters.transaction_type}
                  </span>
                )}
                {filters.property_type && (
                  <span className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs px-2 py-1 rounded">
                    {filters.property_type}
                  </span>
                )}
                {filters.department && (
                  <span className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs px-2 py-1 rounded">
                    {filters.department}
                  </span>
                )}
                {selectedPriceRange !== null && (
                  <span className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs px-2 py-1 rounded">
                    {PRICE_RANGES[selectedPriceRange].label}
                  </span>
                )}
                {selectedBedrooms !== null && (
                  <span className="bg-[#D4AF37]/20 text-[#D4AF37] text-xs px-2 py-1 rounded">
                    {BEDROOM_OPTIONS[selectedBedrooms].label} hab
                  </span>
                )}
              </div>
            </div>

            <Input
              type="email"
              placeholder={t('search.enterEmail', 'tu@email.com')}
              value={saveEmail}
              onChange={(e) => setSaveEmail(e.target.value)}
              className="bg-black/40 border-white/10 text-white mb-4"
              data-testid="save-search-email"
            />

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowSaveModal(false)}
                className="flex-1 border-white/10 text-white/60 hover:text-white"
              >
                {t('common.cancel', 'Cancelar')}
              </Button>
              <Button
                onClick={handleSaveSearch}
                disabled={savingSearch}
                className="flex-1 bg-[#D4AF37] text-black hover:bg-[#FFE182]"
                data-testid="confirm-save-search-btn"
              >
                {savingSearch ? t('common.saving', 'Guardando...') : t('search.save', 'Guardar')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
